import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, AlertCircle, CheckCircle, Users, Scale, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function WinningPostRules() {
  const navigate = useNavigate();

  const rules = [
    {
      number: 1,
      title: "Net Winnings Only",
      icon: <DollarSign className="w-5 h-5" />,
      content: "All winning posts must reflect NET winnings only.",
      detail: "Net winnings = Total money won minus your total bets or buy-ins for that round.",
      example: "If you win $120 but contributed $40 in bets, your posted winnings should be $80."
    },
    {
      number: 2,
      title: "No Gross Amounts",
      icon: <AlertCircle className="w-5 h-5" />,
      content: "Do not post total pots, gross winnings, or combined group payouts.",
      detail: "Only your individual net result should be posted."
    },
    {
      number: 3,
      title: "Per Round Reporting",
      icon: <CheckCircle className="w-5 h-5" />,
      content: "Each winning post must reflect a single round or event.",
      detail: "Do not combine multiple rounds into one post."
    },
    {
      number: 4,
      title: "Accurate Reporting",
      icon: <Scale className="w-5 h-5" />,
      content: "Members are expected to post truthful and accurate results.",
      detail: "Misrepresentation of winnings may result in removal of posts or account restrictions."
    },
    {
      number: 5,
      title: "Friendly Competition",
      icon: <Users className="w-5 h-5" />,
      content: "All winnings are based on friendly competition between members.",
      detail: "Bundle Golf Club does not manage or process any payments."
    },
    {
      number: 6,
      title: "Disputes",
      icon: <MessageSquare className="w-5 h-5" />,
      content: "Any disputes regarding winnings should be resolved between players.",
      detail: "Bundle Golf Club is not responsible for disagreements between members."
    }
  ];

  return (
    <div className="min-h-screen bg-bundle-cream">
      {/* Header */}
      <div className="bg-bundle-green text-white px-4 py-6">
        <div className="max-w-3xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10 mb-4 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <h1 className="font-heading font-bold text-2xl md:text-3xl">Winning Post Rules</h1>
          <p className="font-body text-white/80 mt-2">
            Guidelines for fair and consistent competition
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-bundle-cream-dark p-6 mb-6">
          <p className="font-body text-gray-700 leading-relaxed">
            To keep competition fair and consistent across the platform, all members must follow these guidelines when posting winnings:
          </p>
        </div>

        {/* Rules */}
        <div className="space-y-4">
          {rules.map((rule) => (
            <div 
              key={rule.number}
              className="bg-white rounded-xl shadow-sm border border-bundle-cream-dark p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-bundle-green/10 rounded-full flex items-center justify-center flex-shrink-0 text-bundle-green">
                  {rule.icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-lg text-bundle-green mb-2">
                    {rule.number}. {rule.title}
                  </h3>
                  <p className="font-body text-gray-700 mb-2">
                    {rule.content}
                  </p>
                  <p className="font-body text-gray-600 text-sm">
                    {rule.detail}
                  </p>
                  {rule.example && (
                    <div className="mt-3 bg-bundle-gold/10 border border-bundle-gold/30 rounded-lg p-3">
                      <p className="font-body text-sm text-bundle-green">
                        <strong>Example:</strong> {rule.example}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Agreement Note */}
        <div className="mt-8 bg-bundle-green/5 border border-bundle-green/20 rounded-xl p-6 text-center">
          <p className="font-body text-bundle-green">
            By posting winnings, members agree to follow these rules to maintain fair and competitive play.
          </p>
        </div>
      </div>
    </div>
  );
}
